import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardTitle, CardBody, Row, Col, Label } from 'reactstrap';
import { useSelector } from 'react-redux';
import Select from 'react-select';
import VendorBranchSelector from '../../components/VendorBranchSelector';
import { apiService } from '../../constants/ApiService';
import './TeacherTimeTableBoard.css';

const ROW_HEIGHT = 72;

const DAYS = [
  { day_id: 1, day_name: 'Monday' },
  { day_id: 2, day_name: 'Tuesday' },
  { day_id: 3, day_name: 'Wednesday' },
  { day_id: 4, day_name: 'Thursday' },
  { day_id: 5, day_name: 'Friday' },
  { day_id: 6, day_name: 'Saturday' },
  { day_id: 7, day_name: 'Sunday' },
];

const parseTimeToMinutes = (value) => {
  if (!value) return 0;

  const parts = String(value).split(':');
  const hour = Number(parts[0] || 0);
  const minute = Number(parts[1] || 0);

  return hour * 60 + minute;
};

const minutesToTimeValue = (totalMinutes) => {
  const hour = Math.floor(totalMinutes / 60);
  const minute = totalMinutes % 60;

  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00`;
};

const format12HourTime = (value) => {
  if (!value) return '';

  const parts = String(value).split(':');
  let hour = Number(parts[0] || 0);
  const minute = Number(parts[1] || 0);

  const ampm = hour >= 12 ? 'PM' : 'AM';
  hour = hour % 12;
  if (hour === 0) hour = 12;

  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')} ${ampm}`;
};

const buildTimeSlots = (dayStartTime, dayEndTime, slotMinutes) => {
  if (!dayStartTime || !dayEndTime) return [];

  const start = parseTimeToMinutes(dayStartTime);
  const end = parseTimeToMinutes(dayEndTime);
  const step = Number(slotMinutes || 60);

  const safeEnd = end > start ? end : start + step;

  const slots = [];
  for (let t = start; t < safeEnd; t += step) {
    slots.push({
      time: t,
      label: format12HourTime(minutesToTimeValue(t)),
    });
  }

  if (!slots.length) {
    slots.push({
      time: start,
      label: format12HourTime(minutesToTimeValue(start)),
    });
  }

  return slots;
};

const buildDayLayouts = (days = [], items = []) => {
  const result = {};

  days.forEach((d) => {
    const dayItems = items
      .filter((x) => Number(x.day_id) === Number(d.day_id))
      .map((x) => ({
        ...x,
        _start: parseTimeToMinutes(x.start_time),
        _end: parseTimeToMinutes(x.end_time),
      }))
      .sort((a, b) => a._start - b._start);

    const groups = [];
    let currentGroup = [];
    let currentGroupEnd = -1;

    dayItems.forEach((item) => {
      if (currentGroup.length === 0) {
        currentGroup = [item];
        currentGroupEnd = item._end;
      } else if (item._start < currentGroupEnd) {
        currentGroup.push(item);
        currentGroupEnd = Math.max(currentGroupEnd, item._end);
      } else {
        groups.push(currentGroup);
        currentGroup = [item];
        currentGroupEnd = item._end;
      }
    });

    if (currentGroup.length) groups.push(currentGroup);

    const laidOut = [];

    groups.forEach((group) => {
      const colEndTimes = [];

      group.forEach((item) => {
        let colIndex = colEndTimes.findIndex((end) => end <= item._start);

        if (colIndex === -1) {
          colIndex = colEndTimes.length;
          colEndTimes.push(item._end);
        } else {
          colEndTimes[colIndex] = item._end;
        }

        item._colIndex = colIndex;
      });

      const totalCols = colEndTimes.length;

      group.forEach((item) => {
        laidOut.push({
          ...item,
          _totalCols: totalCols,
        });
      });
    });

    result[d.day_id] = laidOut;
  });

  return result;
};

const TeacherTimeTableBoard = () => {
  const { user } = useSelector((state) => state.authState || {});

  const userProfile =
    user?.user_profile && typeof user?.user_profile === 'object'
      ? user.user_profile
      : user?.UserProfile || {};

  const profileName = String(
    userProfile?.user_profile ||
      user?.user_profile ||
      userProfile?.profile_name ||
      user?.profile_name ||
      '',
  )
    .trim()
    .toLowerCase();

  const isAdminOrSuperAdmin =
    profileName === 'admin' ||
    profileName === 'superadmin' ||
    profileName === 'super admin';

  const getUserVendorId = () =>
    userProfile?.vendor_id ||
    userProfile?.vendorId ||
    userProfile?.VendorID ||
    user?.vendor_id ||
    user?.vendorId ||
    user?.VendorID ||
    '';

  const getUserBranchId = () =>
    userProfile?.branch_id ||
    userProfile?.branchId ||
    userProfile?.BranchID ||
    user?.branch_id ||
    user?.branchId ||
    user?.BranchID ||
    '';

  const [selectionData, setSelectionData] = useState({
    vendor_id: '',
    branch_id: '',
  });

  const [selectedTeacherOption, setSelectedTeacherOption] = useState(null);
  const [loading, setLoading] = useState(false);

  const [board, setBoard] = useState({
    status: false,
    Message: '',
    day_start_time: null,
    day_end_time: null,
    slot_minutes: 60,
    teacher_count: 0,
    teachers: [],
  });

  useEffect(() => {
    setSelectionData((prev) => ({
      vendor_id: prev?.vendor_id || getUserVendorId() || '',
      branch_id: prev?.branch_id || getUserBranchId() || '',
    }));
  }, [user]);

  const loadBoard = useCallback(async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams({
        vendorId: String(selectionData?.vendor_id || ''),
        branchId: String(selectionData?.branch_id || ''),
      });

      const response = await apiService.get(
        `api/TimeTableMaster/GetTeacherWeeklyTimeTable?${params.toString()}`,
      );

      if (response?.status === true) {
        setBoard({
          status: true,
          Message: response?.Message || response?.message || '',
          day_start_time: response?.day_start_time || null,
          day_end_time: response?.day_end_time || null,
          slot_minutes: response?.slot_minutes || 60,
          teacher_count: response?.teacher_count || 0,
          teachers: Array.isArray(response?.teachers) ? response.teachers : [],
        });
      } else {
        setBoard({
          status: false,
          Message: response?.Message || response?.message || 'No data found',
          day_start_time: null,
          day_end_time: null,
          slot_minutes: 60,
          teacher_count: 0,
          teachers: [],
        });
      }
    } catch (error) {
      console.error(error);
      alert('Something went wrong');
      setBoard({
        status: false,
        Message: 'Something went wrong',
        day_start_time: null,
        day_end_time: null,
        slot_minutes: 60,
        teacher_count: 0,
        teachers: [],
      });
    } finally {
      setLoading(false);
    }
  }, [selectionData?.vendor_id, selectionData?.branch_id]);

  useEffect(() => {
    if (selectionData?.vendor_id || selectionData?.branch_id) {
      loadBoard();
    }
  }, [selectionData?.vendor_id, selectionData?.branch_id, loadBoard]);

  useEffect(() => {
    setSelectedTeacherOption(null);
  }, [selectionData?.vendor_id, selectionData?.branch_id]);

  const teacherOptions = useMemo(() => {
    const rawTeachers = Array.isArray(board?.teachers) ? board.teachers : [];

    return rawTeachers
      .map((teacher) => ({
        value: String(teacher?.faculty_id || ''),
        label: teacher?.faculty_name || 'Teacher',
      }))
      .filter((x) => x.value);
  }, [board?.teachers]);

  const displayedTeachers = useMemo(() => {
    const rawTeachers = Array.isArray(board?.teachers) ? board.teachers : [];

    if (!isAdminOrSuperAdmin) {
      return rawTeachers;
    }

    if (!selectedTeacherOption?.value) {
      return rawTeachers;
    }

    return rawTeachers.filter(
      (teacher) =>
        String(teacher?.faculty_id || '') ===
        String(selectedTeacherOption.value),
    );
  }, [board?.teachers, isAdminOrSuperAdmin, selectedTeacherOption]);

  return (
    <div className='teacher-time-table-page'>
      <VendorBranchSelector
        user={user || {}}
        selectionData={selectionData || { vendor_id: '', branch_id: '' }}
        setSelectionData={setSelectionData}
        errorMessage={{}}
        accessButton={true}
      />

      {/* Card 2: Timetable Board */}
      <Card className='card-box pd-20 mb-20'>
        <CardBody>
          {isAdminOrSuperAdmin && (
            <Row className='mt-3'>
              <Col md='4'>
                <Label className='form-label'>Teacher</Label>
                <Select
                  options={teacherOptions}
                  value={selectedTeacherOption}
                  onChange={(option) => setSelectedTeacherOption(option)}
                  placeholder='Select Teacher...'
                  isClearable
                  isSearchable
                />
              </Col>
            </Row>
          )}
        </CardBody>
        <CardTitle className='border-bottom p-3 mb-0 d-flex justify-content-between align-items-center'>
          <span>Teacher Wise Weekly Timetable</span>
          <span className='text-muted small'>
            Total Teachers: {displayedTeachers?.length || 0}
          </span>
        </CardTitle>

        <CardBody>
          {loading ? (
            <div className='text-center p-5'>Loading...</div>
          ) : Array.isArray(displayedTeachers) &&
            displayedTeachers.length > 0 ? (
            <div className='teacher-grid-list'>
              {displayedTeachers.map((teacher, teacherIndex) => {
                const teacherItems = Array.isArray(teacher?.items)
                  ? teacher.items
                  : [];

                const slots = buildTimeSlots(
                  board?.day_start_time,
                  board?.day_end_time,
                  board?.slot_minutes,
                );

                const minuteHeight =
                  ROW_HEIGHT / Number(board?.slot_minutes || 60);

                const startMinute = parseTimeToMinutes(
                  board?.day_start_time || '00:00:00',
                );

                const totalHeight = (slots.length || 0) * ROW_HEIGHT;
                const dayLayouts = buildDayLayouts(DAYS, teacherItems);

                return (
                  <div
                    className='teacher-week-card'
                    key={teacher?.faculty_id || teacherIndex}
                  >
                    <div className='teacher-week-title'>
                      <div>
                        <h5 className='mb-1'>
                          {teacher?.faculty_name || 'Teacher'}
                        </h5>
                        <div className='teacher-week-subtitle'>
                          Total Periods:{' '}
                          {teacher?.total_periods || teacherItems.length || 0}
                        </div>
                      </div>

                      <div className='teacher-week-subtitle'>
                        {format12HourTime(board?.day_start_time)} -{' '}
                        {format12HourTime(board?.day_end_time)}
                      </div>
                    </div>

                    <div className='teacher-board-wrapper'>
                      <div className='teacher-board'>
                        <div className='teacher-board-header'>
                          <div className='teacher-time-header-cell'>Time</div>

                          {DAYS.map((day) => (
                            <div
                              key={day.day_id}
                              className='teacher-day-header-cell'
                            >
                              <div className='teacher-day-name'>
                                {day.day_name}
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className='teacher-board-body'>
                          <div
                            className='teacher-time-column'
                            style={{ height: totalHeight }}
                          >
                            {slots.map((slot) => (
                              <div
                                key={slot.label}
                                className='teacher-time-row-label'
                                style={{ height: ROW_HEIGHT }}
                              >
                                {slot.label}
                              </div>
                            ))}
                          </div>

                          {DAYS.map((day) => {
                            const items = dayLayouts[day.day_id] || [];

                            return (
                              <div
                                key={day.day_id}
                                className='teacher-day-column'
                                style={{ height: totalHeight }}
                              >
                                {slots.map((slot) => (
                                  <div
                                    key={`${teacher?.faculty_id || teacherIndex}_${day.day_id}_${slot.label}`}
                                    className='teacher-day-grid-row'
                                    style={{ height: ROW_HEIGHT }}
                                  />
                                ))}

                                {items.map((item, index) => {
                                  const top =
                                    (item._start - startMinute) * minuteHeight +
                                    4;

                                  const height = Math.max(
                                    (item._end - item._start) * minuteHeight -
                                      8,
                                    56,
                                  );

                                  const width = `calc(${100 / item._totalCols}% - 8px)`;
                                  const left = `calc(${(100 / item._totalCols) * item._colIndex}% + 4px)`;

                                  return (
                                    <div
                                      key={item.id || `${day.day_id}_${index}`}
                                      className='teacher-timetable-card'
                                      style={{ top, height, left, width }}
                                    >
                                      <div className='teacher-tt-title'>
                                        {item.subject_name || 'Subject'}
                                      </div>

                                      <div className='teacher-tt-time'>
                                        {format12HourTime(item.start_time)} -{' '}
                                        {format12HourTime(item.end_time)}
                                      </div>

                                      <div className='teacher-tt-meta'>
                                        <strong>Class:</strong>{' '}
                                        {item.class_name || '-'}
                                      </div>

                                      <div className='teacher-tt-meta'>
                                        <strong>Batch:</strong>{' '}
                                        {item.batch_name || '-'}
                                      </div>

                                      <div className='teacher-tt-meta'>
                                        <strong>Students:</strong>{' '}
                                        {item.student_count || 0}
                                      </div>

                                      {!!item.student_names_text && (
                                        <div className='teacher-tt-students'>
                                          {item.student_names_text}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className='text-center p-4 text-muted'>
              {board?.Message || 'No teacher timetable found'}
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default TeacherTimeTableBoard;
